import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import config from "../../config";
import { useSelector } from "react-redux";
import { sendChatMessage, getStrategistQuestions, getStrategistStrategy } from "../../actions/aiAction";
import FeedbackPrompt from "./FeedbackPrompt";
import "./AssistChat.css";

const serverConfig = { withCredentials: true };

const HINTS = ["challenge", "mirror", "positive", "strategise", "discuss"];

const PROFILE_LABELS = {
  challenger: "Challenge mode",
  mirror: "Mirror mode",
  therapist: "Positive mode",
  strategist: "Strategist mode",
  discuss: "Discuss mode",
};

const PROFILE_COLORS = {
  challenger: "#7c3aed",
  mirror: "#0891b2",
  therapist: "#be185d",
  strategist: "#f17501",
  discuss: "#16a34a",
};

const renderContent = (text) =>
  text.split(/(\*[^*]+\*)/g).map((part, i) =>
    part.startsWith("*") && part.endsWith("*")
      ? <strong key={i}>{part.slice(1, -1)}</strong>
      : part
  );

const INITIAL_MESSAGE = {
  role: "assistant",
  content: "Hey — I've read your node and updates. What's on your mind? We're in discuss mode by default — just talk. You can also try challenge, mirror, positive, or strategise.",
  profile: null,
};

// ─── Strategist inline flow ────────────────────────────────────────────────

const StrategistFlow = ({ nodeId, onDone, onApplied, chatHistory = [], initialState, onStateChange }) => {
  // phase: "loading_questions" | "questions" | "loading_strategy" | "result" | "error"
  const [phase, setPhase] = useState(initialState?.phase ?? "loading_questions");
  const [error, setError] = useState(initialState?.error ?? null);
  const [opening, setOpening] = useState(initialState?.opening ?? "");
  const [questions, setQuestions] = useState(initialState?.questions ?? []);
  const [selectedOptions, setSelectedOptions] = useState(initialState?.selectedOptions ?? {});
  const [freeText, setFreeText] = useState(initialState?.freeText ?? "");
  const [reasoning, setReasoning] = useState(initialState?.reasoning ?? "");
  const [actions, setActions] = useState(initialState?.actions ?? []);
  const [selectedActions, setSelectedActions] = useState(() => new Set(initialState?.selectedActions ?? []));
  const [feedback, setFeedback] = useState(initialState?.feedback ?? "");
  const [rounds, setRounds] = useState(initialState?.rounds ?? []);
  const [lastFailedAction, setLastFailedAction] = useState(initialState?.lastFailedAction ?? null);
  const [applying, setApplying] = useState(false);

  // Initial fetch only when no restored state
  useEffect(() => {
    if (!initialState) fetchQuestions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push state up so parent can persist (localStorage / cross-mount survival)
  useEffect(() => {
    onStateChange?.({
      phase, error, opening, questions, selectedOptions, freeText,
      reasoning, actions, selectedActions: Array.from(selectedActions),
      feedback, rounds, lastFailedAction,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, error, opening, questions, selectedOptions, freeText, reasoning, actions, selectedActions, feedback, rounds, lastFailedAction]);

  const buildCurrentAnswers = () => {
    const answers = questions.map((q) => {
      const selected = selectedOptions[q.id] || null;
      const parts = [];
      if (selected) parts.push(selected);
      if (freeText.trim()) parts.push(`Additional context: ${freeText.trim()}`);
      return { question: q.question, answer: parts.join(". ") || "(no specific preference)" };
    });
    if (questions.length === 0 && freeText.trim()) {
      answers.push({ question: "Additional context from user", answer: freeText.trim() });
    }
    return answers;
  };

  const fetchQuestions = async (history, { resetForm = true } = {}) => {
    setPhase("loading_questions");
    setError(null);
    if (resetForm) {
      setSelectedOptions({});
      setFreeText("");
    }
    try {
      const data = await getStrategistQuestions(nodeId, history, null, chatHistory);
      setOpening(data.opening || "");
      setQuestions(data.questions || []);
      setLastFailedAction(null);
      setPhase("questions");
    } catch (err) {
      setLastFailedAction({ type: "fetchQuestions", history });
      setError(err.response?.data?.message || "Failed to load questions.");
      setPhase("error");
    }
  };

  // Called when user clicks "Strategise" — asks LLM if it has enough context first.
  const evaluateAndProceed = async () => {
    const currentAnswers = buildCurrentAnswers();
    setPhase("loading_strategy");
    setError(null);
    try {
      const evalData = await getStrategistQuestions(nodeId, rounds, currentAnswers, chatHistory);
      if (evalData.readyToStrategise) {
        // LLM has enough — call strategy directly
        const data = await getStrategistStrategy(nodeId, currentAnswers, rounds, chatHistory);
        setReasoning(data.reasoning || "");
        setActions(data.actions || []);
        setSelectedActions(new Set(data.actions.map((_, i) => i)));
        setFeedback("");
        setLastFailedAction(null);
        setPhase("result");
      } else {
        // LLM wants more info — show follow-up questions
        setOpening(evalData.opening || "");
        setQuestions(evalData.questions || []);
        setSelectedOptions({});
        setFreeText("");
        setLastFailedAction(null);
        setPhase("questions");
      }
    } catch (err) {
      setLastFailedAction({ type: "evaluate" });
      setError(err.response?.data?.message || "Failed to generate strategy.");
      setPhase("error");
    }
  };

  // Smart retry — replays the failed call, preserves user's typed work.
  const retry = () => {
    if (lastFailedAction?.type === "evaluate") {
      evaluateAndProceed();
    } else if (lastFailedAction?.type === "fetchQuestions") {
      fetchQuestions(lastFailedAction.history, { resetForm: false });
    } else {
      fetchQuestions(rounds, { resetForm: false });
    }
  };

  const handleRefine = () => {
    const newRound = {
      questionsAndAnswers: questions.map((q) => ({
        question: q.question,
        answer: selectedOptions[q.id] || "(skipped)",
      })),
      freeText,
      strategy: { reasoning, actions },
      acceptedActions: actions.filter((_, i) => selectedActions.has(i)),
      feedback,
    };
    const newHistory = [...rounds, newRound];
    setRounds(newHistory);
    fetchQuestions(newHistory);
  };

  const handleApply = async () => {
    if (applying) return;
    const committedActions = actions.filter((_, i) => selectedActions.has(i));
    if (committedActions.length === 0) return;
    setApplying(true);
    try {
      const payload = JSON.stringify({ reasoning, actions: committedActions });
      await axios.post(
        `${config.apiUrl}/api/v1/addupdate`,
        { postId: nodeId, updateNote: `[Dmyor Assist] ${payload}` },
        serverConfig
      );
      if (onApplied) onApplied();
      onDone(`Committed ${committedActions.length} action${committedActions.length > 1 ? "s" : ""} to the node.`);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to log actions.");
      setApplying(false);
    }
  };

  const toggleAction = (i) => {
    setSelectedActions((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const toggleOption = (qId, opt) => {
    setSelectedOptions((prev) => ({ ...prev, [qId]: prev[qId] === opt ? null : opt }));
  };

  return (
    <div className="ac-strat-wrap">
      {(phase === "loading_questions" || phase === "loading_strategy") && (
        <div className="ac-strat-loading">
          <div className="st-bar-track"><div className="st-bar-fill" /></div>
          <span className="ac-strat-loading-text">
            {phase === "loading_questions" ? "Reading your journey..." : "Mapping your next moves..."}
          </span>
        </div>
      )}

      {phase === "error" && (
        <div className="ac-strat-error">
          <p>{error}</p>
          <button className="ac-strat-btn ac-strat-btn-primary" onClick={retry}>
            Retry
          </button>
          <button className="ac-strat-btn ac-strat-btn-secondary" onClick={() => onDone(null)}>
            Cancel
          </button>
        </div>
      )}

      {phase === "questions" && (
        <div className="ac-strat-section">
          {opening && <p className="ac-strat-opening">{opening}</p>}
          {questions.map((q, qi) => (
            <div key={q.id} className="ac-strat-question">
              <p className="ac-strat-q-text"><span className="ac-strat-qnum">{qi + 1}.</span> {q.question}</p>
              {q.type === "mcq" && q.options && (
                <div className="ac-strat-options">
                  {q.options.map((opt) => (
                    <button
                      key={opt}
                      className={`ac-strat-option ${selectedOptions[q.id] === opt ? "selected" : ""}`}
                      onClick={() => toggleOption(q.id, opt)}
                    >
                      {selectedOptions[q.id] === opt && <i className="bx bx-check" />}
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          <textarea
            className="ac-strat-textarea"
            placeholder="Anything to add or clarify?"
            rows={2}
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
          />
          <div className="ac-strat-footer">
            <button className="ac-strat-btn ac-strat-btn-secondary" onClick={() => onDone(null)}>Cancel</button>
            <button className="ac-strat-btn ac-strat-btn-primary" onClick={evaluateAndProceed}>Strategise</button>
          </div>
        </div>
      )}

      {phase === "result" && (
        <div className="ac-strat-section">
          {reasoning && (
            <div className="ac-strat-reasoning">
              <p className="ac-strat-reasoning-label">Why this strategy</p>
              <p className="ac-strat-reasoning-text">{reasoning}</p>
            </div>
          )}
          <p className="ac-strat-actions-hint">Select actions you agree with:</p>
          {actions.map((action, i) => {
            const checked = selectedActions.has(i);
            return (
              <div
                key={i}
                className={`ac-strat-action ${checked ? "selected" : ""}`}
                onClick={() => !applying && toggleAction(i)}
              >
                <div className="ac-strat-action-check">{checked && <i className="bx bx-check" />}</div>
                <span>{action}</span>
              </div>
            );
          })}
          <textarea
            className="ac-strat-textarea"
            placeholder="Not quite right? What would you change?"
            rows={2}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            disabled={applying}
          />
          {applying && <p className="ac-strat-applying">Logging actions...</p>}
          <div className="ac-strat-footer">
            <button className="ac-strat-btn ac-strat-btn-ghost" onClick={() => onDone(null)} disabled={applying}>
              Cancel
            </button>
            <button className="ac-strat-btn ac-strat-btn-secondary" onClick={handleRefine} disabled={applying}>
              Refine
            </button>
            <button
              className="ac-strat-btn ac-strat-btn-primary"
              disabled={selectedActions.size === 0 || applying}
              onClick={handleApply}
            >
              Commit {selectedActions.size > 0 ? `(${selectedActions.size})` : ""}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main chat component ───────────────────────────────────────────────────

const AssistChat = ({ nodeId, onClose, messages: persistedMessages, onMessagesChange, onApplied, updates, strategistState, onStrategistStateChange }) => {
  const feedbackEligible = useSelector((state) => state.feedback.eligible);
  const [messages, setMessages] = useState(persistedMessages ?? [INITIAL_MESSAGE]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeProfile, setActiveProfile] = useState("discuss");
  const [suggestedProfile, setSuggestedProfile] = useState(null);
  const [strategistOpen, setStrategistOpen] = useState(!!strategistState);
  const [showFeedback, setShowFeedback] = useState(false);
  const strategistWarnedRef = useRef(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const updateMessages = (updater) => {
    setMessages((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      onMessagesChange?.(next);
      return next;
    });
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, strategistOpen]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const detectProfile = (text) => {
    const lower = text.toLowerCase();
    const map = {
      // challenger — explicit only, "be honest" is too common in normal speech
      challenge: "challenger", challenger: "challenger",
      challange: "challenger", chalenge: "challenger",
      "push back": "challenger", "push me": "challenger",
      "be harsh": "challenger",
      // mirror
      mirror: "mirror", reflect: "mirror", reflection: "mirror",
      // therapist / positive
      positive: "therapist", therapist: "therapist",
      therapy: "therapist", "cheer me": "therapist",
      // strategist — only explicit intent words, never generic phrases
      strategise: "strategist", strategize: "strategist",
      strategy: "strategist", strategist: "strategist",
      stratagise: "strategist", stratagize: "strategist",
      strat: "strategist",
      // discuss
      discuss: "discuss", discussion: "discuss",
      explore: "discuss",
    };
    for (const [kw, prof] of Object.entries(map)) {
      if (lower.includes(kw)) return prof;
    }
    return null;
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setSuggestedProfile(null); // clear any pending suggestion on new send

    const detectedProfile = detectProfile(text);

    // Intercept strategist — open inline flow instead of sending to chat
    // BUT: if the message is clearly signalling a different mode, skip the intercept
    // so the fallback detection can suggest switching first
    const therapistSignals = ["motivat", "positive", "encourage", "cheer", "feeling down", "need support", "scared", "afraid", "anxious", "overwhelm", "speak positive", "be positive", "demotivat", "positive talk"];
    const wantsOutOfStrategist = therapistSignals.some((w) => text.toLowerCase().includes(w));

    // If the message looks like a general knowledge or discussion question, don't intercept —
    // let it go to the LLM so the strategist can decide to hand off to discuss.
    const knowledgeQuestionWords = ["how did", "how do", "how does", "what is", "what are", "what was", "why did", "why do", "who is", "who are", "tell me about", "explain", "can you explain", "what's the"];
    const lowerText = text.toLowerCase();
    const looksLikeKnowledgeQuestion = knowledgeQuestionWords.some((w) => lowerText.startsWith(w)) || (lowerText.includes("?") && !lowerText.includes("what should i") && !lowerText.includes("what do i") && !lowerText.includes("how should i"));

    if ((detectedProfile === "strategist" || activeProfile === "strategist") && !wantsOutOfStrategist && !looksLikeKnowledgeQuestion) {
      // Only warn if the last Dmyor Assist update has no real user update after it
      const allUpdates = updates || [];
      const lastAssistIdx = allUpdates.map((u) => u.update?.startsWith("[Dmyor Assist]")).lastIndexOf(true);
      const hasUpdateAfterAssist = lastAssistIdx !== -1 && allUpdates.slice(lastAssistIdx + 1).some((u) => !u.update?.startsWith("[Dmyor Assist]"));
      const needsUpdateFirst = lastAssistIdx !== -1 && !hasUpdateAfterAssist;

      if (needsUpdateFirst && !strategistWarnedRef.current) {
        strategistWarnedRef.current = true;
        updateMessages((prev) => [
          ...prev,
          { role: "user", content: text, profile: null },
          {
            role: "assistant",
            content: "Before strategising again, it's worth posting an update on how the last action items went. That keeps your node meaningful and helps me give better strategy next time.\n\nOnce you've shared what happened, type strategise to continue.",
            profile: "strategist",
          },
        ]);
        setInput("");
        return;
      }
      strategistWarnedRef.current = false;
      updateMessages((prev) => [...prev, { role: "user", content: text, profile: null }]);
      setInput("");
      setStrategistOpen(true);
      return;
    }

    const prof = detectedProfile || activeProfile;
    const newUserMsg = { role: "user", content: text, profile: null };
    const updatedMessages = [...messages, newUserMsg];
    updateMessages(updatedMessages);
    setInput("");
    setLoading(true);

    try {
      const history = updatedMessages.map((m) => ({ role: m.role, content: m.content }));
      const data = await sendChatMessage(nodeId, history, prof);
      const newActiveProfile = data.detectedProfile || activeProfile;
      setActiveProfile(newActiveProfile);

      // Client-side fallback: detect intent the LLM missed and suggest the right profile
      // Works from ANY active mode — never suggests the mode already active
      const lastUserText = text.toLowerCase();
      const llmMissedSwitch = !data.suggestedProfile;

      let clientSuggestion = null;
      if (llmMissedSwitch) {
        const therapistWords = ["motivat", "positive", "encourage", "cheer", "feeling down", "need support", "scared", "afraid", "anxious", "overwhelm", "low", "i need help", "can't do", "cant do", "speak positive", "be positive"];
        const actionWords = ["plan", "what do i do", "just tell me", "move forward", "get out", "next step", "action"];
        const mirrorWords = ["reflect", "mirror", "what do you see", "show me pattern"];
        const challengerWords = ["challenge me", "push back", "push me", "be harsh"];

        if (therapistWords.some((w) => lastUserText.includes(w)) && newActiveProfile !== "therapist") {
          clientSuggestion = "therapist";
        } else if (actionWords.some((w) => lastUserText.includes(w)) && newActiveProfile !== "strategist") {
          clientSuggestion = "strategist";
        } else if (mirrorWords.some((w) => lastUserText.includes(w)) && newActiveProfile !== "mirror") {
          clientSuggestion = "mirror";
        } else if (challengerWords.some((w) => lastUserText.includes(w)) && newActiveProfile !== "challenger") {
          clientSuggestion = "challenger";
        }
      }

      setSuggestedProfile(data.suggestedProfile || clientSuggestion);
      updateMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply, profile: newActiveProfile },
      ]);
      if (feedbackEligible) setTimeout(() => setShowFeedback(true), 8000);
    } catch (err) {
      updateMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Something went wrong. Please try again.", profile: null, error: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const handleHint = (hint) => {
    setInput(hint + " ");
    inputRef.current?.focus();
  };

  const handleStrategistDone = (confirmMsg) => {
    setStrategistOpen(false);
    onStrategistStateChange?.(null); // clear persisted strategist state — flow finished
    if (confirmMsg) {
      updateMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Done — ${confirmMsg} You can keep chatting or try another mode.`, profile: "strategist" },
      ]);
    }
  };

  return (
    <div className="ac-panel">
      {/* Header */}
      <div className="ac-header">
        <span className="ac-title">
          <i className="bx bx-bulb" />
          Dmyor Assist
          {activeProfile && !strategistOpen && (
            <span className="ac-mode-badge" style={{ background: PROFILE_COLORS[activeProfile] }}>
              {PROFILE_LABELS[activeProfile]}
            </span>
          )}
          {strategistOpen && (
            <span className="ac-mode-badge" style={{ background: PROFILE_COLORS.strategist }}>
              Strategist mode
            </span>
          )}
        </span>
        <button className="ac-close" onClick={onClose}>&times;</button>
      </div>

      {/* Messages */}
      <div className="ac-messages">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`ac-msg ${msg.role === "user" ? "ac-msg-user" : "ac-msg-ai"} ${msg.error ? "ac-msg-error" : ""}`}
          >
            {msg.role === "assistant" && msg.profile && (
              <span className="ac-msg-profile-tag" style={{ color: PROFILE_COLORS[msg.profile] }}>
                {PROFILE_LABELS[msg.profile]}
              </span>
            )}
            <p>{renderContent(msg.content)}</p>
          </div>
        ))}

        {/* Inline Strategist flow */}
        {strategistOpen && (
          <StrategistFlow
            nodeId={nodeId}
            onDone={handleStrategistDone}
            onApplied={onApplied}
            chatHistory={messages}
            initialState={strategistState}
            onStateChange={onStrategistStateChange}
          />
        )}

        {loading && (
          <div className="ac-msg ac-msg-ai ac-msg-typing">
            <span /><span /><span />
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Profile suggestion nudge */}
      {suggestedProfile && !strategistOpen && (
        <div className="ac-profile-suggestion">
          <span>Switch to <strong>{PROFILE_LABELS[suggestedProfile]}</strong>?</span>
          <div className="ac-profile-suggestion-btns">
            <button
              className="ac-profile-suggestion-yes"
              onClick={() => {
                setActiveProfile(suggestedProfile);
                setSuggestedProfile(null);
                if (suggestedProfile === "strategist") setStrategistOpen(true);
              }}
            >
              Switch
            </button>
            <button
              className="ac-profile-suggestion-no"
              onClick={() => setSuggestedProfile(null)}
            >
              Stay
            </button>
          </div>
        </div>
      )}

      {/* Hints */}
      {!strategistOpen && (
        <div className="ac-hints">
          {HINTS.map((h) => (
            <button key={h} className="ac-hint-chip" onClick={() => handleHint(h)}>{h}</button>
          ))}
        </div>
      )}

      {/* Input — hidden while strategist flow is active */}
      {!strategistOpen && (
        <div className="ac-input-row">
          <textarea
            ref={inputRef}
            className="ac-input"
            placeholder="Talk about your record..."
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            disabled={loading}
          />
          <button className="ac-send" onClick={send} disabled={!input.trim() || loading}>
            <i className="bx bx-send" />
          </button>
        </div>
      )}

      {showFeedback && (
        <FeedbackPrompt trigger="ai_assist" onDone={() => setShowFeedback(false)} />
      )}
    </div>
  );
};

export default AssistChat;
